import React from 'react';
import {FormProps, FormRendererProps, FormState, FormSubscriberProps} from '@src/@types/components/Form';
import FormRenderer from './FormRenderer';
import FormWithLoadingIndicator from './FormWithLoadingIndicator';
import ValidationHttpException from '@src/exceptions/ValidationHttpException';
import SharedByRefUrlNavigationApi from '@src/components/SharedByRefUrlNavigationApi/SharedByRefUrlNavigationApi';
import FormFields from './FormFields';
import {FormAPIContext} from '@src/contexts/FormAPIContext';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import FormSubscriber from './FormSubscriber';
import {UseFormProps} from 'react-hook-form';

export default class Form<
        Model extends IEmptyModel = IEmptyModel,
        Props extends FormProps<Model> = FormProps<Model>,
        State extends FormState = FormState
> extends FormWithLoadingIndicator<Model, Props, State> {
    protected formFieldsComponent = FormFields;
    protected formRendererComponent = FormRenderer;
    protected formSubscriberComponent = FormSubscriber;

    protected loaderOverlayRef: React.RefObject<HTMLDivElement>;
    protected loaderContentContainerRef: React.RefObject<HTMLDivElement>;
    protected formApiContextValueObject?: React.ContextType<typeof FormAPIContext>;

    constructor(props: Props) {
        super(props);

        this.loaderOverlayRef = React.createRef<HTMLDivElement>();
        this.loaderContentContainerRef = React.createRef<HTMLDivElement>();
    }

    protected async onSubmit(data: Record<string, any>): Promise<any> {
        this.setState({error: undefined});

        return super.onSubmit(data);
    }

    protected async onSubmitRequestFailure(error: Error): Promise<any> {
        return super.onSubmitRequestFailure(error).catch(error => {
            if (!(error instanceof ValidationHttpException)) {
                this.setState({error});
            }

            throw error;
        });
    }

    protected getFormAPIContextValue(): React.ContextType<typeof FormAPIContext> {
        this.formApiContextValueObject ??= {
            refetchModel: this.props.refetchModel,
            overlayRef: this.loaderOverlayRef,
            loaderContentContainerRef: this.loaderContentContainerRef,
            displayOverlay: (state = true) => {
                if (this.state.displayOverlay !== state) {
                    this.setState({displayOverlay: state});
                }
            }
        };
        this.formApiContextValueObject.overlayDisplayed = this.state.displayOverlay === true;

        return this.formApiContextValueObject;
    }

    protected getFormRendererComponent(): React.ComponentType<FormRendererProps<Model>> {
        return this.props.formRendererComponent ?? this.formRendererComponent;
    }

    protected getFormSubscriberComponent(): React.ComponentType<FormSubscriberProps<Model>> {
        return this.props.formSubscriberComponent ?? this.formSubscriberComponent;
    }

    protected getFormAdditionalProps(): UseFormProps | undefined {
        return undefined;
    }

    render() {
        const Loader = this.getLoadingIndicator();
        const Renderer = this.getFormRendererComponent();
        const Subscriber = this.getFormSubscriberComponent();

        return <FormAPIContext.Provider value={this.getFormAPIContextValue()}>
            <SharedByRefUrlNavigationApi ref={this.redirectRef}>
                <Loader
                        loading={this.getLoadingIndicatorState()}
                        forceDisplay={this.state.displayOverlay}
                        innerRef={this.loaderOverlayRef}
                        contentContainerRef={this.loaderContentContainerRef}
                >
                    <Subscriber
                            formName={this.props.formName}
                            model={this.props.model}
                            modelOptions={this.props.modelOptions}
                            config={this.config}
                    >
                        <Renderer
                                fieldsComponent={this.formFieldsComponent}
                                formFieldDesignContextOptions={this.formFieldDesignContextOptions}
                                error={this.state.error}
                                formName={this.props.formName}
                                customFields={this.customFields}
                                customTabsContentWrappers={this.customTabsContentWrappers}
                                customTabsControlWrappers={this.customTabsControlWrappers}
                                model={this.props.model}
                                modelOptions={this.props.modelOptions}
                                config={this.config}
                                controls={this.controls}
                                context={this.props.context}
                                yupScheme={this.validationScheme}
                                initialValues={this.initialValues}
                                onSubmitSuccess={this.onSubmit}
                                onSubmitFail={this.onSubmitPrevented}
                                onSubmitRequestSuccess={this.onSubmitRequestSuccessInForm}
                                onSubmitRequestFail={this.onSubmitRequestFailInForm}
                                skipFormComponent={this.props.skipFormComponent}
                                formProps={this.getFormAdditionalProps()}
                        />
                    </Subscriber>
                </Loader>
            </SharedByRefUrlNavigationApi>
        </FormAPIContext.Provider>;
    }
}
